/* ExposureDetailsViewRenderer.js */

const Registrations = require("../../../../../../../RegistrationNamespaces");
const ExposureDispositions = require("../../ExposureDispositions");
const ExposureJustification = require("./ExposureJustification");
const ExposureConfigEvidence = require("./ExposureConfigEvidence");
const ExposureCheckLedger = require("./ExposureCheckLedger");
const ExposureSnapshotStatus = require("./ExposureSnapshotStatus");
const FileName = require("@gluenetworks/steve_utilities").fileName;

const NO_ENTRY_MESSAGE = "No qualification entry found for this CVE — re-run Qualify CVE Exposure.";
const LOAD_ERROR_MESSAGE = "Unable to load CVE qualification details.";

/**
 * A Device field as text, or "" when it is not text.
 *
 * Several `detected*` fields are declared `IType.String` on the Device entity while real documents
 * hold structs. `String(value)` on one of those yields "[object Object]", which is worse than
 * showing nothing: it puts a non-fact in front of an operator reading evidence for a CVE verdict.
 * Numbers and booleans are still worth rendering, so only objects are refused.
 */
function asDisplayString(value) {
    if (value === null || value === undefined || typeof value === "object") {
        return "";
    }
    return String(value).trim();
}

/**
 * Blade SKUs out of `detectedChassis`, which is slot-keyed on a real chassis device:
 * `{"1": {sku, serialNumber, active, role, software}, "2": {...}}`.
 *
 * Tolerant by the same rule the backend's hardware readers follow: a wrong-typed or malformed
 * field contributes nothing and never throws. A plain string is accepted too, since the field is
 * declared a string and some devices do hold one.
 *
 * @returns {String[]} Deduped, in slot order. Empty when there is no chassis identity to show.
 */
function chassisSkus(chassis) {
    const direct = asDisplayString(chassis);
    if (direct) {
        return [direct];
    }
    if (!chassis || typeof chassis !== "object") {
        return [];
    }
    const skus = [];
    Object.keys(chassis).forEach((slot) => {
        const blade = chassis[slot];
        const sku = blade && typeof blade === "object" ? asDisplayString(blade.sku) : "";
        if (sku && skus.indexOf(sku) < 0) {
            skus.push(sku);
        }
    });
    return skus;
}

class ExposureDetailsViewRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("exposure-details-view-gl");
        this._rootElem.component = this._model;
        this._logger = this._model.logger;
        this._viewActionsAdded = false;
        // Guards against a second step landing while the first one's advisory/rule reads are still
        // in flight: two overlapping steps would race to render, and the loser's content would sit
        // under the winner's stepper position.
        this._stepping = false;
        // Keyboard stepping is bound at the document, not the root element: the root only receives
        // key events while something inside it has focus, and after a click on a row the operator's
        // focus is wherever the previous view left it.
        document.addEventListener("keydown", (this._onKeyDown = this._onKeyDown.bind(this)));
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/

    _showDefaultMessageDisplay(message) {
        return this._model.gameEditor.showDefaultMessageView({message: message}).catch((err) => {
            this._logger.warn(
                {error: err},
                "ExposureDetailsViewRenderer._showDefaultMessageDisplay error"
            );
        });
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/

    shutdown() {
        this._removeViewActions();
        document.removeEventListener("keydown", this._onKeyDown);
        return Promise.resolve().then(
            () => {
                this._model = undefined;
                this._rootElem.remove();
                this._rootElem = undefined;
            },
            (err) => {
                this._logger.warn({error: err}, "ExposureDetailsViewRenderer.shutdown() error.");
            }
        );
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     * @returns {Promise} An ES6 promise that lets the caller know when the component has finished rendering.
     */
    render() {
        return this._model
            .whenModelIsReady()
            .then(() => {
                return this._renderEntry();
            })
            .then(() => {
                this._addViewActions();
            })
            .catch((err) => {
                // message/stack are pulled out deliberately: no bunyan error serializer is
                // registered for this logger, and an Error's own properties are non-enumerable, so
                // `{error: err}` alone serializes a real failure to `error: {}`. A drawer that
                // renders nothing then reports a warning that says nothing about why.
                this._logger.warn(
                    {
                        error: err,
                        errorMessage: (err && err.message) || String(err),
                        errorStack: err && err.stack,
                    },
                    "ExposureDetailsViewRenderer.render() warning"
                );
                if (err === "NoEntry") {
                    return this._showDefaultMessageDisplay(NO_ENTRY_MESSAGE);
                }
                return this._showDefaultMessageDisplay(LOAD_ERROR_MESSAGE);
            });
    }

    get rootElement() {
        return this.getRootElement();
    }

    getRootElement() {
        return this._rootElem;
    }

    get model() {
        return this.getModel();
    }

    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS          */
    /* START PRIVATE RENDER METHODS            */
    /*******************************************/

    /**
     * Layout: a full-width hero header over a two-column body grid. The main
     * (left) column carries the narrative — verdict rationale and evidence &
     * audit; the side (right) column carries the compact facts — severity and
     * remediation. The grid collapses to a single column on narrow screens
     * (see the layout styles).
     */
    _renderEntry() {
        return Promise.resolve().then(() => {
            while (this._rootElem.firstChild) {
                this._rootElem.removeChild(this._rootElem.firstChild);
            }
            const entry = this._model.getEntry();
            const advisory = this._model.getAdvisoriesById().get(entry.advisoryId) || {};

            this._rootElem.appendChild(this._buildHeader(entry, advisory));

            const body = document.createElement("exposure-details-body");
            const main = document.createElement("div");
            main.className = "details-main";
            const side = document.createElement("div");
            side.className = "details-side";

            // Three sections split by PROVENANCE (D4), not by one "Evidence & audit" heap. The old
            // single section put "Matched tags" (observed on this device) two rows above "Rule
            // version" (an engine bookkeeping number) under one heading, so a reader had no way to
            // tell which facts came from the device, which came from the advisory, and which are
            // just run metadata — and the metadata bulked up the section enough to bury the
            // evidence that actually justifies the verdict.
            // The LEDGER LEADS. It is the only element that states both what decided the verdict
            // and what was never run, and the un-run line is the one thing on the page an operator
            // can act on. It also keeps the panel's shape predictable across a 97-row list: every
            // CVE renders the same rungs whether one check ran or five.
            main.appendChild(this._buildCheckLedgerSection(entry));
            main.appendChild(this._buildRationaleSection(entry));
            main.appendChild(this._buildDeviceEvidenceSection(entry));
            const advisoryEvidence = this._buildAdvisoryEvidenceSection(entry);
            if (advisoryEvidence) {
                main.appendChild(advisoryEvidence);
            }
            main.appendChild(this._buildAuditSection(entry));

            const remediation = this._buildRemediationSection(entry);
            if (remediation) {
                side.appendChild(remediation);
            }

            // The rail carries remediation and nothing else now that severity reads from the
            // header. Remediation is suppressed on a cleared verdict (D3), so on those rows the
            // rail has nothing at all — and an empty third of the panel is worse than no rail.
            body.appendChild(main);
            if (side.firstChild) {
                body.appendChild(side);
            } else {
                body.classList.add("no-side");
            }
            this._rootElem.appendChild(body);
        });
    }

    _buildHeader(entry, advisory) {
        const header = document.createElement("exposure-details-header");

        const titleRow = document.createElement("div");
        titleRow.className = "details-title-row";

        const title = document.createElement("h2");
        title.className = "details-title";
        // Focusable without joining the tab order: stepping to another CVE moves focus here to say
        // which one is now on screen (see _focusHeading).
        title.setAttribute("tabindex", "-1");
        title.textContent = entry.cveId || this._model.getCveId();
        titleRow.appendChild(title);

        // Stepper and advisory link share the row's right end so the title keeps the left.
        const titleActions = document.createElement("div");
        titleActions.className = "details-title-actions";
        const stepper = this._buildStepper();
        if (stepper) {
            titleActions.appendChild(stepper);
        }
        if (titleActions.firstChild) {
            titleRow.appendChild(titleActions);
        }
        header.appendChild(titleRow);

        const chips = document.createElement("div");
        chips.className = "details-chips";

        const sir = (advisory.sir || "").trim().toLowerCase();
        const severityChip = document.createElement("exposure-severity");
        severityChip.setAttribute("for", sir || "none");
        severityChip.textContent = advisory.sir || "—";
        chips.appendChild(severityChip);

        // Beside the SIR chip, because they are the same kind of fact measured two ways: the
        // vendor's rating and the numeric score behind it. As its own section it was a heading and
        // a bordered card wrapped around three characters, holding a third of the panel's width.
        chips.appendChild(
            this._buildMetric("CVSS", advisory.cvssBaseScore ? String(advisory.cvssBaseScore) : "—")
        );

        // Chip and qualifier share a wrapper so the qualifier stays bound to the verdict it
        // weakens. As bare siblings in the chips flex row they would sit an equal gap from the
        // chip and from the advisory name beside it, and read as belonging to either.
        const dispositionGroup = document.createElement("span");
        dispositionGroup.className = "details-disposition";
        const dispositionChip = document.createElement("exposure-score");
        dispositionChip.setAttribute("for", (entry.score || "").toLowerCase());
        dispositionChip.textContent = ExposureDispositions.dispositionLabel(entry.score);
        dispositionGroup.appendChild(dispositionChip);

        // Qualifier outside the chip, same treatment as the grid — including the tooltip and the
        // weight class, which the grid had and this header did not. The qualifiers point in both
        // directions now: "(version only)" warns that no configuration was consulted, while the
        // config ones say the verdict rests on evidence read off this device. The drawer header is
        // exactly where someone decides whether to act, so it is the last place to leave that
        // unexplained.
        const qualified = ExposureDispositions.dispositionLabel(entry.score, entry.reasonCode);
        const unqualified = ExposureDispositions.dispositionLabel(entry.score);
        if (qualified !== unqualified) {
            const qualifier = document.createElement("span");
            qualifier.className =
                "disposition-qualifier " + ExposureDispositions.qualifierWeight(entry.reasonCode);
            qualifier.textContent = qualified.slice(unqualified.length).trim();
            qualifier.setAttribute(
                "title",
                ExposureDispositions.qualifierTooltip(entry.reasonCode)
            );
            dispositionGroup.appendChild(qualifier);
        }
        chips.appendChild(dispositionGroup);

        // ONE affordance for the advisory, not two. This chip and the link at the title row's right
        // end both opened the same view, and the chip spent itself printing an id that the "From the
        // advisory" section already lists under Contributing advisories. So the chip keeps the click
        // and says what it does; the id rides the tooltip for anyone who wants it before clicking.
        //
        // The device chip is gone for the same reason: "Evidence from this device" states the
        // hostname, hardware, serial and software properly, a few rows below, where a reader looking
        // for device identity actually goes.
        if (entry.advisoryId) {
            const advisoryLabel = document.createElement("button");
            advisoryLabel.type = "button";
            advisoryLabel.className = "details-advisory details-advisory-link";
            advisoryLabel.textContent = "Open advisory details";
            advisoryLabel.title = advisory.displayName || entry.advisoryId;
            advisoryLabel.addEventListener("click", () => {
                this._openAdvisoryDetails(entry.advisoryId);
            });
            chips.appendChild(advisoryLabel);
        }

        header.appendChild(chips);
        return header;
    }

    /**
     * Prev / "12 of 43" / next, for working down the list without returning to it between CVEs.
     *
     * Reviewing exposure is a sequence, not a lookup: an operator triaging a device reads Affected
     * rows in order, and a drawer that can only be left by going back charges them a full round trip
     * per CVE. The position indicator is half the point — it says how much of the list is left, which
     * the table cannot once its scroll offset stops meaning anything.
     *
     * No wraparound at the ends. A disabled button says "that was the last one", where a jump back
     * to row 1 would read as a step and quietly restart the review.
     *
     * @returns {Element|null} Null when there is nothing to step to (a single-row device).
     */
    _buildStepper() {
        const order = this._model.getOrderedCveIds();
        const index = order.indexOf(this._model.getCveId());
        if (order.length < 2 || index < 0) {
            return null;
        }
        const stepper = document.createElement("exposure-details-stepper");

        stepper.appendChild(this._buildStepButton(-1, "Previous", "‹", order[index - 1]));

        const position = document.createElement("span");
        position.className = "stepper-position";
        position.textContent = index + 1 + " of " + order.length;
        stepper.appendChild(position);

        stepper.appendChild(this._buildStepButton(1, "Next", "›", order[index + 1]));

        return stepper;
    }

    /**
     * @param {Number} delta -1 or 1, the step this button takes when pressed.
     * @param {String} label Direction word, used in the accessible name.
     * @param {String} glyph Chevron shown to sighted users — the accessible name carries the words.
     * @param {String} [targetCveId] Absent at the ends of the list, which is what disables the
     *   button. Named in the tooltip when present: "the next CVE" is a weaker promise than its id.
     */
    _buildStepButton(delta, label, glyph, targetCveId) {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "stepper-button";
        button.dataset.step = delta > 0 ? "next" : "prev";
        button.textContent = glyph;
        const arrow = delta > 0 ? "→" : "←";
        button.setAttribute(
            "aria-label",
            label + " CVE" + (targetCveId ? ", " + targetCveId : "") + " (" + arrow + ")"
        );
        button.setAttribute(
            "title",
            targetCveId
                ? label + ": " + targetCveId + "  " + arrow
                : "No " + label.toLowerCase() + " CVE"
        );
        // Distinguishes "disabled because the list ends here" from "disabled while a step loads":
        // only the latter is ever re-enabled.
        button.dataset.hasTarget = targetCveId ? "true" : "false";
        if (!targetCveId) {
            button.disabled = true;
        } else {
            button.addEventListener("click", () => {
                this._stepBy(delta);
            });
        }
        return button;
    }

    /**
     * Moves the drawer to the adjacent CVE in the table's order and redraws in place.
     *
     * @param {Number} delta -1 for the previous CVE, 1 for the next.
     * @returns {Promise} Resolves once the new CVE is on screen. A step at either end, or while
     *   another step is still loading, resolves without changing anything.
     */
    _stepBy(delta) {
        if (this._stepping) {
            return Promise.resolve();
        }
        const order = this._model.getOrderedCveIds();
        const index = order.indexOf(this._model.getCveId());
        const target = index < 0 ? undefined : order[index + delta];
        if (!target) {
            return Promise.resolve();
        }
        this._stepping = true;
        this._setStepperDisabled(true);
        return (
            this._model
                .stepTo(target)
                // The operator can leave the drawer while a step's reads are in flight — the view is
                // shut down by then, and there is nothing left to draw the CVE into.
                .then(() => (this._rootElem ? this._renderEntry() : null))
                .then(() => {
                    this._stepping = false;
                    if (!this._rootElem) {
                        return null;
                    }
                    // A new CVE is a new read: carrying the previous one's scroll offset over would
                    // drop the operator into the middle of an unrelated verdict.
                    this._rootElem.scrollTop = 0;
                    this._focusHeading();
                    return target;
                })
                .catch((err) => {
                    this._stepping = false;
                    this._logger.warn({error: err}, "ExposureDetailsViewRenderer._stepBy() error");
                    if (this._rootElem) {
                        // The drawer still shows the CVE it was on, so its stepper goes back to live.
                        this._setStepperDisabled(false);
                    }
                })
        );
    }

    /**
     * Keyboard stepping: ← and →, the same keys the buttons advertise.
     *
     * Only fires for keys pressed against the drawer itself or against a bare document (which is
     * where focus lands after the click that opened this view). That keeps the shortcut out of
     * modals and out of any field where an arrow key means "move the caret".
     */
    _onKeyDown(event) {
        if (
            event.defaultPrevented ||
            event.altKey ||
            event.ctrlKey ||
            event.metaKey ||
            event.shiftKey
        ) {
            return;
        }
        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") {
            return;
        }
        const target = event.target;
        const withinDrawer = this._rootElem && this._rootElem.contains(target);
        if (!withinDrawer && target !== document.body && target !== document.documentElement) {
            return;
        }
        if (
            target &&
            (target.isContentEditable ||
                ["INPUT", "TEXTAREA", "SELECT"].indexOf(target.tagName) >= 0)
        ) {
            return;
        }
        event.preventDefault();
        this._stepBy(event.key === "ArrowLeft" ? -1 : 1);
    }

    _setStepperDisabled(disabled) {
        const buttons = this._rootElem.querySelectorAll(".stepper-button");
        Array.prototype.forEach.call(buttons, (button) => {
            // Re-enabling is only ever undoing this method's own disable — a button disabled
            // because it sits at the end of the list keeps that state via its missing target.
            if (disabled) {
                button.disabled = true;
            } else if (button.dataset.hasTarget === "true") {
                button.disabled = false;
            }
        });
    }

    /**
     * Moves focus to the heading of the CVE just stepped to.
     *
     * A step swaps the drawer's contents without moving anything on screen, which a screen reader
     * has no way to notice — and it cannot be fixed with a live region, because the re-render
     * replaces that region along with the rest of the header and a freshly inserted live region
     * announces nothing. Focusing the heading states plainly where the operator now is (the CVE id),
     * and leaves ← → working for the next step from exactly where they are.
     *
     * It also restores focus the re-render destroyed: every element the operator could have been
     * on, including the stepper button they just pressed, was replaced.
     */
    _focusHeading() {
        const heading = this._rootElem.querySelector(".details-title");
        if (heading) {
            heading.focus();
        }
    }

    /**
     * "How we checked" — one row per check that bears on this verdict, in cost order, with the
     * deciding one marked.
     *
     * The runtime row carries an action when evidence would settle the CVE and was never collected.
     * That control opens a CLI session on a production device, so it is chromed rather than
     * rendered as inline link text like every other button in this panel — a deliberate break from
     * the house style, because visible weight is how an interface communicates consequence, and a
     * device-touching action styled as a cross-reference misrepresents itself.
     */
    _buildCheckLedgerSection(entry) {
        const section = this._buildSection("How we checked");
        const rows = ExposureCheckLedger.buildLedger(entry);
        if (!rows.length) {
            return section;
        }

        const table = document.createElement("div");
        table.className = "details-ledger-rows";
        rows.forEach((row) => {
            const line = document.createElement("div");
            line.className = "details-ledger-row" + (row.decided ? " decided" : "");

            const title = document.createElement("b");
            title.textContent = row.title;
            line.appendChild(title);

            const finding = document.createElement("span");
            finding.className = "ledger-finding";
            finding.textContent = row.finding;
            line.appendChild(finding);

            const outcome = document.createElement("span");
            outcome.className = "ledger-outcome";
            outcome.textContent = row.outcome;
            line.appendChild(outcome);

            if (row.runnable) {
                line.appendChild(this._buildRunCheckControl());
            }
            table.appendChild(line);
        });

        section.appendChild(table);
        return section;
    }

    /**
     * The control that collects runtime evidence.
     *
     * Says what it will do before it does it. It runs every outstanding check on THIS DEVICE, not
     * just the CVE whose row it sits on, because the backend unions a device's pending commands
     * into one CLI session — scoping per CVE would open a session per row.
     */
    _buildRunCheckControl() {
        const wrapper = document.createElement("span");
        wrapper.className = "ledger-action";

        const button = document.createElement("button");
        button.className = "run-check-button";
        button.type = "button";
        button.textContent = "Run checks on this device";
        button.setAttribute(
            "title",
            "Opens a CLI session on this device and runs the show commands the advisories publish. " +
                "Resolves every outstanding check on the device, not only this CVE."
        );
        button.addEventListener("click", () => {
            // Disabled on click, BEFORE the dialog opens: a second click would queue a second CLI
            // session, and leaving the button live until the operator confirms would make that
            // guard depend on the modal veil swallowing pointer events.
            button.disabled = true;
            const restore = () => {
                button.disabled = false;
                button.textContent = "Run checks on this device";
            };
            // CONFIRM FIRST. This is the only control in the drawer that touches production
            // hardware, and one click used to be the whole ceremony — the launcher's own header
            // claimed the caller had confirmed, which was never true. The dialog names the device
            // and the scope, because "are you sure" without the specifics is a click-through.
            this._confirmDeviceChecks()
                .then(() => {
                    // Only now. The label read "Launching…" while the dialog was still asking
                    // whether to launch.
                    button.textContent = "Launching…";
                    return this._runDeviceChecks();
                })
                .then(() => {
                    // The promise settles when the job is LAUNCHED, not when it finishes, and this
                    // drawer does not poll. Saying "running" here would leave a control claiming
                    // live progress it is not watching; say what is actually known instead.
                    button.textContent = "Checks running — re-open this CVE for results";
                    return null;
                })
                .catch((err) => {
                    restore();
                    // Cancelling is not a failure. displayConfirmation rejects with
                    // {userRejected: true} on the reject button and on dismissal, so logging it
                    // would file a warning every time somebody thought better of it.
                    if (err && err.userRejected) {
                        return;
                    }
                    this._logger.warn({error: err}, "ExposureDetailsViewRenderer._runDeviceChecks");
                });
        });
        wrapper.appendChild(button);

        const warning = document.createElement("span");
        warning.className = "details-rule-warning";
        warning.textContent = "runs commands on the live device";
        wrapper.appendChild(warning);

        return wrapper;
    }

    /**
     * The device's display name, or the caller's stand-in.
     *
     * The fallback is a PARAMETER because the two callers need different words, and the DEFAULT is
     * the filename-safe one. The confirmation dialog reads this inside a sentence; the justification
     * zip reads it as a filename token, and FileName rewrites a space to an underscore — so a
     * sentence fragment defaulted here lands in a download as "..._this_device_CVE-...". Folding the
     * two call sites onto one helper introduced exactly that, and defaulting to the token keeps a
     * caller that forgets the argument from reintroducing it.
     *
     * @param {String} [fallback] Word to use when the device has neither a name nor an id.
     */
    _deviceLabel(fallback) {
        const device = this._model.getDevice();
        if (device && device.name) {
            return device.name;
        }
        if (device && typeof device.getId === "function") {
            return device.getId();
        }
        return fallback || "device";
    }

    /**
     * Confirm before opening a CLI session on production hardware.
     *
     * Names the device and the SCOPE, because the scope is the surprising part: one click resolves
     * every outstanding check on the box, not the CVE whose row was clicked. The backend unions a
     * device's pending commands into a single session, so per-CVE scoping would open a session per
     * row — which makes the wide behaviour correct and makes saying so out loud necessary.
     *
     * The commands themselves are deliberately NOT listed. The scorer knows them, but the verdict
     * does not carry them (VerdictEntry declares probeCommands and the entry builder never writes
     * it), and the union spans advisories this drawer is not showing. Listing this CVE's command
     * would understate what is about to run, which is worse than describing the set honestly.
     *
     * Rejects with {userRejected: true} when declined — see the caller.
     */
    _confirmDeviceChecks() {
        return this._model
            .getUserFeedback()
            .displayConfirmation(
                "This opens a CLI session on " +
                    this._deviceLabel("this device") +
                    " and runs the show commands the advisories publish.\r\n\r\n" +
                    "It resolves every outstanding runtime check on this device, not only this CVE.\r\n\r\n" +
                    "Results appear the next time this CVE is re-opened.",
                {
                    title: "Run checks on the live device",
                    confirmDetails: {text: "Run checks", type: "destructive"},
                    rejectDetails: {text: "Cancel", type: "text"},
                    notDismissable: true,
                }
            );
    }

    /**
     * Launch the device-scoped runtime collection. Resolves when LAUNCHED, not when complete.
     *
     * The launcher is required HERE rather than at module scope on purpose: it pulls in the options
     * dialog and, through it, the whole UI-control chain, which reads `navigator` at import time.
     * A module that otherwise just builds DOM nodes should not carry that at load, and this is the
     * only path that needs it.
     */
    _runDeviceChecks() {
        const QualifyCvesLauncher = require("../../../../../../lib/QualifyCvesLauncher");
        const model = this._model;
        return QualifyCvesLauncher.launchRuntimeCheck({
            TriggerTypeEntity: model.getTriggerTypeEntity(),
            orgId: model.getActiveOrgId(),
            // Same multi shape the Exposure tab's Qualify action uses, so both paths land on one
            // work-target convention rather than two.
            workTarget: {
                method: "multi",
                category: "entity",
                entityType: "Device",
                ids: [model.getDeviceId()],
            },
            modalUserFeedback: model.getUserFeedback(),
            logger: this._logger,
            sourceLabel: "Exposure details",
        });
    }

    _buildRationaleSection(entry) {
        const section = this._buildSection("Verdict rationale");

        const reason = document.createElement("p");
        reason.className = "details-reason";
        reason.textContent = entry.reasonText || "—";
        section.appendChild(reason);

        if (entry.decidedAt) {
            const level = document.createElement("span");
            level.className = "details-decided-at";
            // Text, not colour alone — this has to survive a screen reader and a mono display.
            // The LABEL, not the stored enum: this row shipped reading "Decided on: predicate".
            level.textContent =
                "Decided on: " + ExposureDispositions.decidedAtLabel(entry.decidedAt);
            section.appendChild(level);
        }
        return section;
    }

    /**
     * Evidence observed on THIS DEVICE: what its configuration and runtime state showed. Every
     * row here is something we looked at on the box, which is what makes the section safe to read
     * as "the case against (or for) this device".
     */
    _buildDeviceEvidenceSection(entry) {
        const section = this._buildSection("Evidence from this device");

        // What the device IS, before what it was found to have. Hardware and software identity is
        // frequently the whole verdict — a platform-gated advisory turns on the chassis, a
        // version-only red turns on the release — and reading it here beats leaving the reader to
        // reconcile the drawer against the device's own tabs.
        this._buildDeviceFactRows().forEach((row) => section.appendChild(row));

        // Canonical order (D8), shared with the grid popup and the CSV: what we could NOT
        // establish precedes what we matched. The negatives are the actionable half — they name
        // the checks an operator still has to perform — while matched tags only corroborate a
        // verdict the header already states.
        //
        // notEvaluated is never a row: it needs the verdict's deciding level to be actionable,
        // and that is rendered in the rationale section above.
        const evidence = ExposureDispositions.tagEvidenceSections(entry);
        const matchedTags = (entry.matchedTags || []).join(", ");
        // "Feature check: not performed" over "Matched tags: —" is two rows reporting the same
        // nothing, on a verdict where the ledger's Features rung already said "nothing to check".
        // Suppressed only when BOTH are empty: the moment either carries a tag, the pair renders
        // as before, so the section keeps its one absence convention wherever absence is a finding.
        const noFeatureEvidence =
            !evidence.legacy &&
            !matchedTags &&
            evidence.sections.length === 1 &&
            evidence.sections[0].label === ExposureDispositions.NO_FEATURE_EVIDENCE_LABEL;
        if (noFeatureEvidence) {
            evidence.sections = [];
        }
        evidence.sections.forEach((bucket) => {
            // A section with no tags carries only its hint (the "no feature check" case), so the
            // value must not open with a stray separator.
            const value = bucket.tags.length
                ? bucket.tags.join(", ") + (bucket.hint ? " (" + bucket.hint + ")" : "")
                : bucket.hint || "—";
            section.appendChild(this._buildKeyValue(bucket.label, value));
        });

        // One absence behaviour for the whole section. Before this, "Matched tags" always rendered
        // with an em-dash placeholder while "Missing tags" rendered only when non-empty — two
        // conventions for the same class of information, in adjacent rows.
        if (!noFeatureEvidence) {
            section.appendChild(this._buildKeyValue("Matched tags", matchedTags || "—"));
        }

        const evidenceRows = [].concat(entry.runtimeEvidence || []);
        if (evidenceRows.length > 0) {
            const list = document.createElement("div");
            list.className = "details-evidence-rows";
            evidenceRows.forEach((evidence) => {
                const row = document.createElement("div");
                row.className = "details-evidence-row";
                const command = document.createElement("code");
                command.textContent = evidence.command || "";
                row.appendChild(command);
                const interpretation = document.createElement("span");
                interpretation.className =
                    "evidence-interpretation " + (evidence.interpretation || "");
                interpretation.textContent = evidence.interpretation || "";
                row.appendChild(interpretation);
                if (evidence.outputExcerpt) {
                    const excerpt = document.createElement("pre");
                    excerpt.textContent = evidence.outputExcerpt;
                    row.appendChild(excerpt);
                }
                if (evidence.failureReason) {
                    const failure = document.createElement("span");
                    failure.className = "evidence-failure";
                    failure.textContent = evidence.failureReason;
                    row.appendChild(failure);
                }
                list.appendChild(row);
            });
            section.appendChild(this._buildKeyValue("Runtime evidence", ""));
            section.appendChild(list);
        }

        this._appendConfigEvidence(section, entry);

        return section;
    }

    /**
     * Config-determination evidence: the advisory's own published grep, run against this device's
     * stored configuration.
     *
     * Rows that moved the verdict render in full and first; rows that ran and could not clear the
     * device collapse to one counted line. On the reference device 5 of 8 real checks land in that
     * second group, so rendering them all at equal weight would bury the one row carrying signal
     * under four that carry none.
     */
    _appendConfigEvidence(section, entry) {
        const split = ExposureConfigEvidence.partitionConfigEvidence(entry.configEvidence);
        if (split.legacy || (!split.decisive.length && !split.inconclusive.length)) {
            // Legacy verdicts predate config checks entirely. Rendering an empty section would
            // assert that a check ran and found nothing, which is the opposite claim.
            return;
        }

        section.appendChild(this._buildKeyValue("Configuration evidence", ""));
        const list = document.createElement("div");
        list.className = "details-evidence-rows";

        split.decisive.forEach((row) => {
            list.appendChild(this._buildConfigEvidenceRow(row));
        });

        if (split.inconclusive.length) {
            // ACTUALLY COLLAPSED. This rendered the summary line and then every row it summarized
            // in full underneath, with no disclosure and no style hiding them — so the section was
            // the same wall of rows as before, plus a count. `details` gives real collapse with no
            // JS state to keep in sync and keyboard/screen-reader behaviour for free.
            const disclosure = document.createElement("details");
            disclosure.className = "details-evidence-collapsed";
            // The interpretation class goes on a SPAN inside the summary, mirroring how every row
            // builds its own outcome token. Putting it on <summary> itself left it unstyled: the
            // theme scopes .evidence-interpretation to the row (and now the summary) as a PARENT,
            // so a class on the parent element matches nothing.
            const summary = document.createElement("summary");
            const summaryText = document.createElement("span");
            summaryText.className = "evidence-interpretation inconclusive";
            summaryText.textContent = ExposureConfigEvidence.summarizeInconclusive(
                split.inconclusive
            );
            summary.appendChild(summaryText);
            disclosure.appendChild(summary);
            split.inconclusive.forEach((row) => {
                disclosure.appendChild(this._buildConfigEvidenceRow(row));
            });
            list.appendChild(disclosure);
        }

        section.appendChild(list);
    }

    /** One config check: the command an operator can re-run, what it found, and what that means. */
    _buildConfigEvidenceRow(evidence) {
        const row = document.createElement("div");
        row.className = "details-evidence-row";

        // The COMMAND leads, not the pattern. The pattern is how we implemented the check; the
        // command is how the operator reproduces it.
        const command = document.createElement("code");
        command.textContent = evidence.command || evidence.pattern || "";
        row.appendChild(command);

        const outcome = document.createElement("span");
        outcome.className = "evidence-interpretation " + (evidence.outcome || "");
        outcome.textContent = ExposureConfigEvidence.describeOutcome(
            evidence,
            evidence.linesSearched
        );
        row.appendChild(outcome);

        // The check's CEILING, stated on the row rather than inferred from the verdict afterwards.
        // Muted, because it is a property of the advisory rather than a finding about this device.
        const ceiling = ExposureConfigEvidence.describeCeiling(evidence);
        if (ceiling) {
            const ceilingElem = document.createElement("span");
            ceilingElem.className = "evidence-ceiling";
            ceilingElem.textContent = ceiling;
            row.appendChild(ceilingElem);
        }

        (evidence.matchedLines || []).forEach((line) => {
            const matched = document.createElement("pre");
            // Redacted server-side before persist — the frontend is the wrong layer for that,
            // because the same text also reaches the CSV export and the justification bundle.
            matched.textContent = "L" + line.lineNumber + ": " + line.text;
            row.appendChild(matched);
        });

        // Lines that ALMOST matched a check that cleared this device. The only element on the panel
        // that argues against its own verdict, which is exactly why it renders: a green produced by
        // an over-anchored pattern is indistinguishable from a real one, and these lines are the
        // evidence a reviewer needs to tell them apart.
        const nearMisses = evidence.nearMissLines || [];
        if (nearMisses.length) {
            const warning = document.createElement("p");
            warning.className = "details-rule-warning";
            warning.textContent =
                "This check cleared the device, but " +
                (nearMisses.length === 1 ? "a nearby line" : nearMisses.length + " nearby lines") +
                " almost matched — verify the pattern is not too strict.";
            row.appendChild(warning);
            nearMisses.forEach((line) => {
                const near = document.createElement("pre");
                near.textContent = "L" + line.lineNumber + ": " + line.text;
                row.appendChild(near);
            });
        }

        if (evidence.sourceQuote) {
            // The vendor sentence is the ONLY thing that licenses an absence-based clearance, so
            // it is shown verbatim rather than paraphrased.
            const quote = document.createElement("span");
            quote.className = "evidence-source-quote";
            quote.textContent = "advisory: " + evidence.sourceQuote;
            row.appendChild(quote);
        }

        // ONE restatement is dropped, not the meaning line. When the advisory's own sentence is
        // quoted verbatim directly above, our paraphrase of it is the third telling of one fact —
        // the rationale section said it first. Every other meaning (no snapshot, check did not run,
        // cannot clear the device) is the row's only explanation and always renders.
        const meaning = ExposureConfigEvidence.describeMeaning(evidence);
        const restatesQuote =
            Boolean(evidence.sourceQuote) &&
            meaning === ExposureConfigEvidence.CLEARED_BY_QUOTE_MEANING;
        if (meaning && !restatesQuote) {
            const verdict = document.createElement("span");
            verdict.className = "evidence-failure";
            verdict.textContent = meaning;
            row.appendChild(verdict);
        }

        return row;
    }

    /**
     * Hardware and software identity, straight off the Device record.
     *
     * Only rows with a value render: a device discovered at a level that never learned its serial
     * should show no serial line, not a row of em dashes implying the field was checked and empty.
     *
     * @returns {Element[]} Possibly empty.
     */
    _buildDeviceFactRows() {
        const device = this._model.getDevice();
        let props = {};
        try {
            props =
                (device && typeof device.getPristineProperties === "function"
                    ? device.getPristineProperties()
                    : device) || {};
        } catch (err) {
            this._logger.warn(err, "ExposureDetailsViewRenderer._buildDeviceFactRows() error");
            return [];
        }
        // Chassis and platform are frequently the same string; showing both would read as two
        // facts that happen to agree rather than one fact stated twice.
        //
        // detectedChassis is NOT a string. It is declared IType.String on the Device entity, but a
        // real chassis device holds a slot-keyed struct — {"1": {sku, serialNumber, active, role,
        // software}, "2": {...}} — and that is the normal case, not corruption. Joining it
        // directly rendered "[object Object]" as the device's hardware. Its blade SKUs are the
        // useful part: a platform- or module-gated advisory turns on exactly those.
        const hardware = chassisSkus(props.detectedChassis)
            .concat([asDisplayString(props.detectedPlatform), asDisplayString(props.detectedSku)])
            .filter(Boolean)
            .filter((value, index, all) => all.indexOf(value) === index)
            .join(" · ");
        // Every field goes through asDisplayString for the same reason detectedChassis did: these
        // are declared strings that real documents do not always honour, and String(anObject) puts
        // "[object Object]" in front of an operator rather than omitting a fact we do not have.
        return [
            ["Hostname", asDisplayString(props.detectedHostName)],
            ["Hardware", hardware],
            ["Serial", asDisplayString(props.detectedSerialNumber)],
            [
                "Software",
                [asDisplayString(props.detectedOs), asDisplayString(props.detectedSoftwareVersion)]
                    .filter(Boolean)
                    .join(" "),
            ],
        ]
            .filter(([, value]) => value)
            .map(([key, value]) => this._buildKeyValue(key, value));
    }

    /**
     * Facts that came from the ADVISORY and from the rule extracted out of it — nothing here was
     * observed on the device. Separated because "the vendor said X" and "we saw X on the box"
     * carry very different weight, and the rule provenance in particular is a statement about an
     * LLM extraction, not about this device. Returns null when there is nothing to show.
     */
    _buildAdvisoryEvidenceSection(entry) {
        const rows = [];

        const contributingIds = entry.contributingAdvisoryIds || [];
        const contributing = contributingIds
            .map((id) => {
                const advisory = this._model.getAdvisoriesById().get(id);
                return (advisory && advisory.displayName) || id;
            })
            .join(", ");
        if (contributing) {
            rows.push(["Contributing advisories", contributing]);
        }

        // The header names the advisory only in a tooltip now, so this section is where the id is
        // readable. Rendered ONLY when the contributing list does not already carry it — on most
        // verdicts it does, and printing it twice is what the header change was undoing.
        if (entry.advisoryId && contributingIds.indexOf(entry.advisoryId) === -1) {
            const advisory = this._model.getAdvisoriesById().get(entry.advisoryId);
            rows.unshift(["Advisory", (advisory && advisory.displayName) || entry.advisoryId]);
        }

        // Extraction provenance of the rule that produced this verdict (trust contract: consumers
        // see what produced the verdict) — e.g. "Rule v1 · claude-sonnet-4-6 · extracted Jul 18,
        // 2026".
        const rule = this._model.getRule ? this._model.getRule() : null;
        if (rule) {
            const parts = [];
            if (rule.version !== null && rule.version !== undefined) {
                parts.push("Rule v" + rule.version);
            }
            if (rule.extractedByModel) {
                parts.push(rule.extractedByModel);
            }
            if (rule.extractedAt) {
                parts.push("extracted " + this._toLocaleString(rule.extractedAt));
            }
            rows.push(["Rule provenance", parts.join(" · ")]);
            if (rule.status === "error" && rule.extractionError) {
                rows.push(["Extraction error", rule.extractionError]);
            }
        }

        if (!rows.length) {
            return null;
        }
        const section = this._buildSection("From the advisory");
        rows.forEach(([key, value]) => {
            section.appendChild(this._buildKeyValue(key, value));
        });
        return section;
    }

    /**
     * The configuration this verdict scored, in terms a reviewer can act on.
     *
     * Replaces nothing — it sits beside the raw snapshot id, which stays because it is the value
     * you correlate with a work log. This line is the one that says whether the evidence can be
     * trusted: how much was searched, when the device said it, and whether the device has moved on
     * since. A superseded or missing snapshot is an anomaly (auto re-score is on by default), so it
     * reads as a warning rather than as a stamp.
     */
    _describeConfigSnapshot(entry) {
        // Optional call: the snapshot list is supporting context, so a model that cannot supply it
        // degrades to "no configuration consulted" rather than taking the drawer down with it.
        const snapshots =
            typeof this._model.getConfigSnapshots === "function"
                ? this._model.getConfigSnapshots()
                : [];
        const status = ExposureSnapshotStatus.describeSnapshot(entry, snapshots);
        const evidence = (entry.configEvidence || [])[0] || {};
        const provenance = ExposureSnapshotStatus.describeProvenance(
            evidence.linesSearched,
            status.capturedAt,
            (ts) => this._toLocaleString(ts)
        );
        return provenance ? provenance + " — " + status.detail : status.detail;
    }

    /**
     * Run bookkeeping — which execution produced this verdict and against what inputs. Collapsed
     * by default: it is what you need when reconciling two runs or filing a bug, and noise the
     * rest of the time. It shared a heading with the evidence rows and outnumbered them, so the
     * section that justified the verdict read as mostly engine trivia.
     */
    _buildAuditSection(entry) {
        const section = this._buildSection("Audit trail");

        const details = document.createElement("details");
        details.className = "details-audit";
        const summary = document.createElement("summary");
        // Says what is inside, so the disclosure is worth opening on purpose rather than out of
        // curiosity. <details> is keyboard-operable and announces its expanded state natively.
        summary.textContent = "Run, rule version, signature and snapshot";
        details.appendChild(summary);

        const stamps = document.createElement("div");
        stamps.className = "details-stamps";
        [
            ["Qualified at", this._toLocaleString(entry.qualifiedAt) || "—"],
            ["Run", entry.runId || "—"],
            [
                "Rule version",
                entry.ruleVersionAtRun === null || entry.ruleVersionAtRun === undefined
                    ? "—"
                    : String(entry.ruleVersionAtRun),
            ],
            ["Signature", entry.signatureHashAtRun || "—"],
            ["Snapshot", entry.snapshotIdAtRun || "—"],
            ["Configuration", this._describeConfigSnapshot(entry)],
        ].forEach(([stampKey, stampValue]) => {
            stamps.appendChild(this._buildKeyValue(stampKey, stampValue));
        });
        details.appendChild(stamps);

        section.appendChild(details);
        return section;
    }

    /**
     * Severity facts from the advisory: CVSS base score and first-fixed
     * version — the concrete, ingested data. Threat-feed signals (EPSS, CISA
     * KEV, public-exploit) are not integrated yet and are deliberately not
     * surfaced here.
     */

    /** True when the verdict clears this device, which is what suppresses remediation (D3). */
    _isNotAffected(entry) {
        return (((entry && entry.score) || "") + "").trim().toLowerCase() === "green";
    }

    /**
     * Remediation guidance is plain text only: it is machine-generated content
     * and must never carry a launch/execute/copy-run affordance. Returns null
     * when no guidance exists.
     */
    _buildRemediationSection(entry) {
        const rule = this._model.getRule();
        const text =
            (rule && rule.recommendedRemediation) || entry.recommendedRemediationAtRun || null;
        if (!text) {
            return null;
        }
        // Nothing to remediate on a Not Affected verdict (D3). The guidance is written for a
        // device the advisory applies to, so rendering it here contradicts the header chip in the
        // same drawer — and a reader who acts on it does unnecessary work on the strength of a
        // page that just told them they were clear.
        if (this._isNotAffected(entry)) {
            return null;
        }
        const section = this._buildSection("Recommended remediation");

        const caption = document.createElement("span");
        caption.className = "remediation-caption";
        caption.textContent = "Machine-generated — verify before acting";
        section.appendChild(caption);

        // Yellow and inconclusive are NOT confirmations: exposure was not established, so the
        // guidance is contingent. Without this the section reads identically on a confirmed red
        // and on a verdict that could not be decided at all.
        const score = (((entry && entry.score) || "") + "").trim().toLowerCase();
        if (score === "yellow" || score === "inconclusive") {
            const contingent = document.createElement("span");
            contingent.className = "remediation-contingent";
            contingent.textContent =
                score === "yellow"
                    ? "Exposure is not confirmed — apply only if you verify the device is affected."
                    : "This device could not be evaluated — confirm exposure before applying.";
            section.appendChild(contingent);
        }

        const guidance = document.createElement("p");
        guidance.className = "remediation-text";
        guidance.textContent = text;
        section.appendChild(guidance);
        return section;
    }

    /**
     * The breadcrumb trail back out of the advisory: Exposure, then the CVE this drawer is showing.
     *
     * The CVE crumb is what makes the advisory a detour rather than an exit. With only the Exposure
     * crumb, reading the advisory behind a verdict cost the operator their place in the drawer AND a
     * fresh scroll through the table to find the row again — so the fastest way back to a CVE was to
     * not open its advisory at all.
     *
     * The crumb carries the trail the drawer itself should show (value.breadCrumb), because the
     * editor rebuilds the view from this object alone.
     */
    _buildBackToExposureTrail() {
        const exposureCrumb = {
            value: {
                fqn: Registrations.DIE_VIEW_EXPOSURE,
            },
            content: "Exposure",
            left: {
                arrow: {
                    direction: "left",
                },
            },
        };
        const cveId = this._model.getCveId();
        return [
            exposureCrumb,
            {
                value: {
                    fqn: Registrations.DIE_VIEW_EXPOSURE_DETAILS,
                    viewState: {cveId: cveId},
                    breadCrumb: [exposureCrumb],
                },
                content: cveId,
                left: {
                    arrow: {
                        direction: "left",
                    },
                },
            },
        ];
    }

    _openAdvisoryDetails(advisoryId) {
        this._model.gameEditor.setActiveView(
            Registrations.DIE_VIEW_ADVISORY_DETAILS,
            this._buildBackToExposureTrail(),
            {
                advisoryId: advisoryId,
            }
        );
    }

    /**
     * Registers the drawer's bottom action-bar button (mirrors how the parent
     * Exposure view registers Qualify/Download). View actions are the shared,
     * editor-level toolbar, so they are added on render and cleared on
     * shutdown — the outgoing view's shutdown empties the bar before the next
     * view fills it.
     */
    _addViewActions() {
        if (this._viewActionsAdded) {
            return;
        }
        this._model.gameEditor.addViewAction({
            type: "normal",
            displayName: "Download justification",
            name: "DownloadJustification",
            handler: (this._onDownloadJustification = this._onDownloadJustification.bind(this)),
        });
        this._viewActionsAdded = true;
    }

    _removeViewActions() {
        if (this._viewActionsAdded && this._model && this._model.gameEditor) {
            this._model.gameEditor.clearViewActions();
        }
        this._viewActionsAdded = false;
    }

    /**
     * Assembles the per-CVE justification bundle (advisory + CPE ranges, rule predicate, extraction
     * telemetry/output, device tags, verdict, audit stamps, org prefs, caveats) and downloads it
     * as a zip. The advisory-wide loads run here, not on drawer open.
     */
    _onDownloadJustification() {
        const gameEditor = this._model.gameEditor;
        gameEditor.disableViewAction("DownloadJustification");
        return this._model
            .loadJustificationData()
            .then((extra) => {
                const entry = this._model.getEntry();
                const device = this._model.getDevice();
                const deviceName = this._deviceLabel();
                const cveId = entry.cveId || this._model.getCveId();
                const fileMap = ExposureJustification.buildJustificationFileMap({
                    cveId: cveId,
                    generatedAt: new Date().toISOString(),
                    exporter: this._exporterInfo(),
                    org: this._orgInfo(),
                    device: {
                        id: device && typeof device.getId === "function" ? device.getId() : null,
                        name: device && device.name ? device.name : null,
                        os: entry.deviceOsAtRun || null,
                        version: entry.deviceVersionAtRun || null,
                    },
                    entry: entry,
                    advisoriesById: extra.advisoriesById,
                    rulesByAdvisoryId: extra.rulesByAdvisoryId,
                    deviceTags: extra.deviceTags,
                    deviceTagsError: extra.deviceTagsError,
                    prefs: extra.prefs,
                });
                const zipName = FileName({
                    solution: "DM",
                    general: "Exposure-Justification",
                    specific: deviceName + "_" + cveId,
                    suffix: "zip",
                });
                return this._model.getIo().fileFactory.saveStringsAsZip(fileMap, zipName);
            })
            .then(() => {
                gameEditor.enableViewAction("DownloadJustification");
            })
            .catch((err) => {
                this._logger.warn(
                    {error: err},
                    "ExposureDetailsViewRenderer._onDownloadJustification() error"
                );
                gameEditor.enableViewAction("DownloadJustification");
            });
    }

    _exporterInfo() {
        const userSession =
            typeof this._model.getUserSession === "function" ? this._model.getUserSession() : null;
        const user = userSession && userSession.user ? userSession.user : null;
        const info = {};
        if (user) {
            if (user.email) {
                info.email = user.email;
            }
            if (user.name) {
                info.name = user.name;
            } else if (user.firstName || user.lastName) {
                info.name = [user.firstName, user.lastName].filter(Boolean).join(" ");
            }
        }
        return info;
    }

    _orgInfo() {
        const userSession =
            typeof this._model.getUserSession === "function" ? this._model.getUserSession() : null;
        const org =
            userSession && userSession.activeOrganization ? userSession.activeOrganization : null;
        if (org) {
            return {id: org.id || null, name: org.name || null};
        }
        return {
            id:
                typeof this._model.getActiveOrgId === "function"
                    ? this._model.getActiveOrgId()
                    : null,
        };
    }

    /*******************************************/
    /* END PRIVATE RENDER METHODS              */
    /* START PRIVATE API METHODS               */
    /*******************************************/

    _buildSection(titleText) {
        const section = document.createElement("exposure-details-section");
        const title = document.createElement("h3");
        title.textContent = titleText;
        section.appendChild(title);
        return section;
    }

    _buildKeyValue(key, value) {
        const row = document.createElement("div");
        row.className = "details-key-value";
        const keyElem = document.createElement("span");
        keyElem.className = "details-key";
        keyElem.textContent = key;
        row.appendChild(keyElem);
        const valueElem = document.createElement("span");
        valueElem.className = "details-value";
        valueElem.textContent = value;
        row.appendChild(valueElem);
        return row;
    }

    /**
     * A labelled value for the header chip row ("CVSS 8.6").
     *
     * Label and value are separate spans inside a flex box rather than siblings sharing a text
     * node: this row builds its children by appending, and a span whose spacing depends on a
     * stylesheet rule someone remembers to write is exactly how the evidence rows ended up
     * rendering "318 lines searchedcan clear this CVE".
     */
    _buildMetric(label, value) {
        const metric = document.createElement("span");
        metric.className = "details-metric";
        const labelElem = document.createElement("span");
        labelElem.className = "details-metric-label";
        labelElem.textContent = label;
        metric.appendChild(labelElem);
        const valueElem = document.createElement("span");
        valueElem.className = "details-metric-value";
        valueElem.textContent = value;
        metric.appendChild(valueElem);
        return metric;
    }

    _toLocaleString(val) {
        const d = new Date(val);
        if (Object.prototype.toString.call(d) === "[object Date]" && !isNaN(d.getTime())) {
            return d.toLocaleDateString() + " " + d.toLocaleTimeString();
        } else {
            return "";
        }
    }

    /*******************************************/
    /*  END PRIVATE API METHODS                */
    /*******************************************/
}

module.exports = ExposureDetailsViewRenderer;
